import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { AwesomeIcon, Body, Heading, Panel } from '@hover/blueprint';
import React, {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useState,
} from 'react';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { Button } from 'src/shared/components';
import { BoxLink, BoxLinkProps } from 'src/shared/components/BoxLink';

interface SignUpLinkProps extends BoxLinkProps {
  children: ReactNode;
  description: string;
  label: string;
  title: string;
}

const PERSPECTIVE = '20rem';

const ZOOM = 1.1;
const ROTATE_X = 100;
const ROTATE_Y = 80;
const SHADOW = 10;

const ROTATION_IDLE = [0, 0, 1];
const SHADOW_IDLE = [10, 10, 0.05];

const ROTATION_OVER = [0, 0, ZOOM];
const SHADOW_OVER = [12.5, 12.5, 0.15];

const StyledPanel = styled(Panel)`
  transition-property: box-shadow, background-color;
  transition-duration: 400ms;
  transition-timing-function: ease;
`;

const SignUpLink = ({
  children,
  description,
  label,
  title,
  testId,
  ...props
}: SignUpLinkProps) => {
  const [{ rotation, shadow }, set] = useSpring(() => ({
    rotation: ROTATION_IDLE,
    shadow: SHADOW_IDLE,
    config: { mass: 5, tension: 350, friction: 40 },
  }));
  const [isMouseOver, setIsMouseOver] = useState(false);

  const transform = rotation.interpolate<string>(
    // @ts-expect-error
    (x, y, scale) =>
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `perspective(${PERSPECTIVE}) rotateX(${x}deg) rotateY(${y}deg) scale(${scale})`,
  );

  const filter = shadow.interpolate<string>(
    // @ts-expect-error
    (x, y, opacity) =>
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `drop-shadow(${x}px ${y}px 10px rgba(0, 0, 0, ${opacity}))`,
  );

  const over = () => set({ rotation: ROTATION_OVER, shadow: SHADOW_OVER });

  const onMouseEnter = () => {
    over();
    setIsMouseOver(true);
  };

  const onMouseLeave = () => {
    set({ rotation: ROTATION_IDLE, shadow: SHADOW_IDLE });
    setIsMouseOver(false);
  };

  const onChildrenMouseMove = ({
    clientX,
    clientY,
    currentTarget,
  }: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
    const { scrollHeight: height, scrollWidth: width } = currentTarget;
    const { left, top } = currentTarget.getBoundingClientRect();

    const x = clientX - left;
    const y = clientY - top;

    const centerX = x - width / 2;
    const centerY = y - height / 2;

    const percentX = x / width;
    const percentY = y / height;

    const [shadowX, shadowY] = SHADOW_IDLE;

    set({
      rotation: [-centerY / ROTATE_Y, centerX / ROTATE_X, ZOOM],
      shadow: [
        shadowX + (1 - percentX) * SHADOW,
        shadowY + (1 - percentY) * SHADOW,
        0.15,
      ],
    });
  };

  const onChildrenMouseLeave = () => over();

  return (
    <BoxLink
      aria-label={label}
      display="flex"
      flexDirection="column-reverse"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      position="relative"
      testId={testId}
      {...props}
    >
      <StyledPanel
        alignItems="center"
        backgroundColor="neutral000"
        boxShadow={isMouseOver ? 'distance600' : 'distance500'}
        display="grid"
        gridColumnGap={200}
        gridTemplateAreas={`
          "title        icon"
          "description  icon"
        `}
        paddingX={600}
        paddingY={500}
      >
        <Heading color="neutral700" gridArea="title" size={200}>
          {title}
        </Heading>
        <Body margin={0} size={400}>
          {description}
        </Body>
        <Button
          gridArea="icon"
          justifySelf="end"
          label={label}
          shape="circle"
          size="large"
          testId={testId ? `${testId}-button` : undefined}
        >
          <AwesomeIcon color="neutral000" icon={faArrowRight} size="medium" />
        </Button>
      </StyledPanel>
      <animated.div
        onMouseLeave={onChildrenMouseLeave}
        onMouseMove={onChildrenMouseMove}
        style={{ transform, filter, willChange: 'transform, filter' }}
      >
        {children}
      </animated.div>
    </BoxLink>
  );
};

export { SignUpLink };
